import { useState } from "react";
import { Check, Lock, ShoppingBag, Sparkles } from "lucide-react";
import { StepShell, BottomBar, PrimaryButton } from "./ui";

const TIER_STYLE = {
  98: "bg-[#138808] text-white",
  85: "bg-[#FF9933] text-[#1f2328]",
  70: "bg-[#1f2328] text-white",
  vicinity: "bg-[#000080] text-white",
};
const fmtType = (t) => (t === "Flat" ? "Apartment" : t || "Property");

const ProjectCard = ({ p, selected, already, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={already}
    data-selected={selected ? "true" : "false"}
    className={`w-full rounded-3xl border bg-white p-4 text-left transition-[border-color,box-shadow,transform] duration-200 active:scale-[0.99] disabled:cursor-default disabled:bg-[#fbf8f3] ${selected ? "border-[#138808] shadow-[0_0_0_3px_rgba(19,136,8,0.12)]" : "border-neutral-200 hover:border-neutral-300"}`}
    data-testid={`project-card-${p.ref}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#138808]" data-testid={`project-match-${p.ref}`}>{p.match_pct}% match</p>
        <p className="mt-1 font-display text-lg leading-snug text-[#1f2328]">
          {[p.bhk, fmtType(p.property_type)].filter(Boolean).join(" ")} in {p.locality}{p.city ? `, ${p.city}` : ""}
        </p>
      </div>
      <span className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${selected ? "border-[#138808] bg-[#138808] text-white" : "border-neutral-300"}`}>
        {selected && <Check className="h-4 w-4" />}
      </span>
    </div>
    {already && <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#138808]/10 px-2.5 py-1 text-xs font-semibold text-[#0f6d06]" data-testid={`project-already-${p.ref}`}><Check className="h-3 w-3" /> Already requested, our team is on it</p>}
    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
      {[["Price", p.price], ["Carpet", p.carpet], ["Possession", p.possession], ["Segment", p.segment]].filter(([, v]) => v).map(([k, v]) => (
        <div key={k}>
          <dt className="text-[11px] uppercase tracking-wide text-neutral-500">{k}</dt>
          <dd className="font-medium text-[#1f2328]">{v}</dd>
        </div>
      ))}
    </dl>
    {p.why?.length > 0 && (
      <ul className="mt-3 flex flex-wrap gap-1.5">
        {p.why.map((w) => (
          <li key={w} className="flex items-center gap-1 rounded-full bg-[#138808]/8 px-2.5 py-1 text-xs text-[#0f6d06]"><Check className="h-3 w-3" />{w}</li>
        ))}
      </ul>
    )}
    {p.relaxed?.length > 0 && <p className="mt-2 text-xs text-neutral-500" data-testid={`project-relaxed-${p.ref}`}>Slightly different {p.relaxed.map((r) => (r === "bhk" ? "configuration" : r)).join(" and ")} from what you chose</p>}
    <p className="mt-3 flex items-center gap-1.5 text-[11px] text-neutral-500"><Lock className="h-3 w-3" /> Project name shared in your MatchMaking presentation</p>
  </button>
);

const Tier = ({ tier, selected, already, toggle, setMany }) => {
  const [expanded, setExpanded] = useState(false);
  const refs = tier.projects.map((p) => p.ref).filter((r) => !already.includes(r));
  const allOn = refs.length > 0 && refs.every((r) => selected.includes(r));
  const shown = expanded ? tier.projects : tier.projects.slice(0, 4);
  return (
    <section className="mt-8" data-testid={`tier-${tier.tier}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${TIER_STYLE[tier.tier] || TIER_STYLE[70]}`}>{tier.tier === "vicinity" ? "Nearby" : `~${tier.tier}%`}</span>
          <h3 className="font-display text-xl text-[#1f2328]">{tier.label}</h3>
          <span className="text-sm text-neutral-500">({tier.projects.length})</span>
        </div>
        {refs.length > 0 && <button type="button" onClick={() => setMany(refs, !allOn)} className="min-h-[44px] shrink-0 whitespace-nowrap text-sm font-semibold text-[#138808]" data-testid={`tier-${tier.tier}-select-all`}>
          {allOn ? "Clear" : "Select all"}
        </button>}
      </div>
      <div className="space-y-3">
        {shown.map((p) => <ProjectCard key={p.ref} p={p} already={already.includes(p.ref)} selected={selected.includes(p.ref)} onToggle={() => toggle(p.ref)} />)}
      </div>
      {tier.projects.length > 4 && !expanded && (
        <button type="button" onClick={() => setExpanded(true)} className="mt-3 min-h-[44px] w-full rounded-full border border-neutral-200 text-sm font-medium text-neutral-700" data-testid={`tier-${tier.tier}-show-more`}>
          Show {tier.projects.length - 4} more
        </button>
      )}
    </section>
  );
};

export const Results = ({ match, onChange, onNext, onBack }) => {
  const selected = match.selected || [];
  const already = match.already || [];
  const found = match.tiers.flatMap((t) => t.projects.map((p) => p.ref));
  const all = found.filter((r) => !already.includes(r));
  const toggle = (ref) => onChange(selected.includes(ref) ? selected.filter((r) => r !== ref) : [...selected, ref]);
  const setMany = (refs, on) => onChange(on ? [...new Set([...selected, ...refs])] : selected.filter((r) => !refs.includes(r)));

  return (
    <StepShell stepKey="results" onBack={onBack} testId="screen-results">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--saffron-deep)]"><Sparkles className="h-4 w-4" /> Namaste — good news</p>
      <h2 className="mt-2 font-display text-[1.8rem] font-medium leading-tight text-[#1f2328] sm:text-4xl" data-testid="results-heading">
        We found {found.length} project{found.length === 1 ? "" : "s"} that suit you.
      </h2>
      <p className="mt-3 text-[15px] text-neutral-600">
        Pick the ones you'd like to explore. We'll prepare a MatchMaking presentation for each and send them to you by email and on WhatsApp chat within 24 hours.
      </p>
      {match.tiers.map((t) => <Tier key={t.tier} tier={t} selected={selected} already={already} toggle={toggle} setMany={setMany} />)}
      <div className="h-40" />
      <BottomBar>
        <div className="space-y-2.5" data-testid="results-cart-bar">
          <PrimaryButton onClick={() => onNext(selected)} disabled={!selected.length} data-testid="request-matchmaking-selected-button">
            <ShoppingBag className="h-5 w-5" /> Request MatchMaking{selected.length ? ` (${selected.length})` : ""}
          </PrimaryButton>
          {all.length > 0 && <button type="button" onClick={() => onNext(all)} className="min-h-[44px] w-full rounded-full border border-[#138808] bg-white text-sm font-semibold text-[#138808]" data-testid="request-matchmaking-all-button">
            Request MatchMaking on {already.length ? "all remaining" : "all"} {all.length} project{all.length === 1 ? "" : "s"}
          </button>}
        </div>
      </BottomBar>
    </StepShell>
  );
};
